/**
 * RootRoute component
 * Handles redirect logic for the root path (/)
 * - If user profile is incomplete, redirects to /profile
 * - If user profile is complete, redirects to /timetracking
 * - Shows loading spinner while checking profile
 */

import React from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useProfileCompletion } from "../hooks/useProfileCompletion";

const RootRoute: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { isComplete, isLoading: profileLoading } = useProfileCompletion();
  const navigate = useNavigate();

  // Show loading spinner while checking auth and profile
  if (authLoading || profileLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // If profile is incomplete, redirect to profile page
  if (!isComplete) {
    return <Navigate to="/profile" replace />;
  }

  // If profile is complete, redirect to home page
  return <Navigate to="/home" replace />;
};

export default RootRoute;
